// ============================================
// use-document.ts — R14 Document mode data hooks
// ============================================
//
// Thin TanStack Query wrappers around the three new backend endpoints:
//   - GET  /api/projects/:projectId/views?type=DOCUMENT
//       → handled by the existing useSavedViews hook; this module
//         re-exports it with a `viewType: 'DOCUMENT'` preset.
//   - GET  /api/views/:viewId/document-snapshot
//       → fetchDocumentSnapshot(viewId) returns the raw Yjs bytes.
//   - POST /api/views/:viewId/document-snapshot
//       → useSaveDocumentSnapshot(viewId) debounces uploads so we
//         don't hammer the backend while the user types.
//
// The auto-save debounce is owned by the EDITOR (DocumentEditor), not
// the hook — the hook returns a `save` callback and the caller decides
// when to fire it. The editor debounces internally because that's
// where the change signal lives.

import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { apiGet } from '@/lib/api';
import { useSavedViews, viewKeys } from '@/hooks/use-views';
import type { SavedView } from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const documentKeys = {
    all: ['document'] as const,
    snapshots: () => [...documentKeys.all, 'snapshot'] as const,
    snapshot: (viewId: string) => [...documentKeys.snapshots(), viewId] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Fetch all DOCUMENT views for a project. Reuses the existing
 * `useSavedViews` hook (which already understands the `type=` query
 * param) so the sidebar / tabs UI get the same cache-invalidation
 * flow as Kanban/Calendar/Gantt.
 */
export function useDocumentViews(projectId: string) {
    return useSavedViews(projectId, 'DOCUMENT');
}

/**
 * Fetch the persisted Yjs snapshot for a single document view.
 * Returns `null` when the view has no snapshot yet (fresh document).
 *
 * The query is `enabled` only when both `viewId` and `enabled` are
 * truthy; pass `enabled: false` to skip the fetch entirely (e.g.
 * while a Yjs sync is in-flight on the WebSocket).
 */
export function useDocumentSnapshot(
    viewId: string | null | undefined,
    options?: { enabled?: boolean }
): UseQueryResult<Uint8Array | null> {
    const enabled = !!viewId && (options?.enabled ?? true);

    return useQuery<Uint8Array | null>({
        queryKey: documentKeys.snapshot(viewId || '__none__'),
        enabled,
        // Snapshots are big — never cache longer than a single session.
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        queryFn: async () => {
            if (!viewId) return null;
            try {
                const response = await fetch(
                    `/api/views/${encodeURIComponent(viewId)}/document-snapshot`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: { Accept: 'application/octet-stream' },
                    }
                );
                if (response.status === 404) return null;
                if (!response.ok) {
                    throw new Error(`Failed to load document snapshot: HTTP ${response.status}`);
                }
                const buf = await response.arrayBuffer();
                if (buf.byteLength === 0) return null;
                return new Uint8Array(buf);
            } catch (err) {
                // Snapshot failures are non-fatal — the editor still
                // works on a fresh Yjs doc + WS sync.
                console.warn('[useDocumentSnapshot] load failed', err);
                return null;
            }
        },
    });
}

/**
 * POST the Yjs snapshot back to the backend. The caller is expected
 * to debounce + coalesce — `DocumentView` sizes the wait from
 * `AUTOSAVE_DEBOUNCE_MS`/`AUTOSAVE_MAX_WAIT_MS` and refuses to start a
 * second request while one is in flight.
 *
 * On success the mutation patches the matching SavedView cache entry
 * with the new `updatedAt` timestamp + `hasDocumentSnapshot` flag so
 * the sidebar's "Last saved X ago" hint updates without a refetch.
 *
 * R26 — the mutation no longer hides failures: it gets an `onError`
 * that logs the transport/HTTP failure, and `saveMutation.error` is
 * available to the caller (TanStack Query already exposes it), which is
 * how `DocumentView` drives its `'error'` save state. Note this hook
 * deliberately does NOT offer retries — `DocumentView` keeps the bytes
 * dirty and schedules a single backoff itself, because react-query's
 * retry bursts would re-upload a full document snapshot several times.
 */
export function useSaveDocumentSnapshot(viewId: string | null | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (bytes: Uint8Array): Promise<{ bytes: number; savedAt: string }> => {
            if (!viewId) throw new Error('No viewId for snapshot upload');
            const response = await fetch(
                `/api/views/${encodeURIComponent(viewId)}/document-snapshot`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/octet-stream' },
                    // Wrap in a Blob — `BodyInit` accepts Blob but not
                    // Uint8Array directly under TS strict mode.
                    // Body init for binary uploads. Cast to `BodyInit` because TS
// strict mode rejects `Uint8Array` directly (it can carry a
// SharedArrayBuffer backing). `BlobPart[]` accepts an ArrayBuffer
// in non-shared mode; the snapshot loader reads bytes back from
// `arrayBuffer()` so the runtime behaviour is identical.
body: new Blob([bytes as BlobPart], { type: 'application/octet-stream' }) as BodyInit,
                }
            );
            if (!response.ok) {
                throw new Error(`Failed to save document snapshot: HTTP ${response.status}`);
            }
            const json = (await response.json()) as { bytes: number; savedAt: string };
            return json;
        },
        // R26: surface (never swallow) the failure. `DocumentView` owns
        // the user-visible status + toast, but the raw reason belongs in
        // the console so a failed upload is diagnosable.
        onError: (error) => {
            // eslint-disable-next-line no-console
            console.error('[useSaveDocumentSnapshot] upload failed', viewId, error);
        },
        onSuccess: (result) => {
            if (!viewId) return;
            // Patch the view list cache so `hasDocumentSnapshot` +
            // `documentSnapshotBytes` reflect the latest write without
            // a round-trip refetch.
            const lists = queryClient.getQueryCache().findAll({
                queryKey: viewKeys.lists(),
            });
            for (const entry of lists) {
                queryClient.setQueryData(entry.queryKey, (old: unknown) => {
                    if (!old || typeof old !== 'object') return old;
                    const data = old as { views?: SavedView[] };
                    if (!Array.isArray(data.views)) return old;
                    return {
                        ...data,
                        views: data.views.map((v) =>
                            v.id === viewId
                                ? {
                                    ...v,
                                    hasDocumentSnapshot: true,
                                    documentSnapshotBytes: result.bytes,
                                    updatedAt: result.savedAt,
                                }
                                : v
                        ),
                    };
                });
            }
        },
    });
}

// ============================================
// HIGHER-LEVEL HELPERS
// ============================================

/**
 * Convenience wrapper around useDocumentSnapshot + useSaveDocumentSnapshot.
 * Returns `{ snapshot, isLoading, save, isSaving }`. The caller owns
 * the debounce policy.
 */
export function useDocumentPersistence(viewId: string | null | undefined) {
    const snapshotQuery = useDocumentSnapshot(viewId);
    const saveMutation = useSaveDocumentSnapshot(viewId);

    const save = useCallback(
        (bytes: Uint8Array) => saveMutation.mutate(bytes),
        [saveMutation]
    );

    return {
        snapshot: snapshotQuery.data ?? null,
        isLoading: snapshotQuery.isLoading,
        saveError: saveMutation.error,
        isSaving: saveMutation.isPending,
        lastSavedAt: saveMutation.data?.savedAt ?? null,
        save,
        refetchSnapshot: snapshotQuery.refetch,
    };
}